import CONFIG from "@/config";
import type {
  Segment,
  SegmentListResponse,
  SegmentCreateRequest,
  SegmentPatchRequest,
  SegmentPreferencePatchRequest,
  SegmentCategory,
  SegmentCategoryCreateRequest,
  SegmentCategoryPatchRequest,
} from "@interfaces/segments/domain";
import type {
  BrandSetView,
  BrandSetListResponse,
} from "@/components/BrandSets/shared/brandSet.types";
import type {
  SelectionsValidateRequest,
  SelectionsValidateResponse,
  SelectionsApplyRequest,
  SelectionsApplyResponse,
} from "@/interfaces/selections/selections.types";
import tokenManager from "@/utils/tokenManager";
import { apiInstances } from "@endpoints/config";
import { type Options } from "ky";
import {
  prepareBrandData,
  prepareBrandsMappingData,
  prepareFeatureStatusData,
  prepareMarketData,
} from "../utils/bootstrapApi";
import { STORAGE_KEY } from "@/constant/export/storage";
import {
  buildAssistantHeaders,
  buildAssistantUrl,
} from "@/components/AIAssistant/utils/assistantApi";
import { clearAssistantStore } from "@/components/AIAssistant/utils/assistantStore";
import { clearAbortController, getAbortSignalForKey } from "./requestManager";

const userService = {
  auth: {
    logout: async () => {
      let redirectUrl = "";
      try {
        await fetch(buildAssistantUrl("logout/"), {
          method: "POST",
          headers: buildAssistantHeaders(),
          credentials: "include",
        });
      } catch (error) {
        console.warn("[AI Assistant] Logout cleanup failed", error);
      }
      try {
        redirectUrl = await apiInstances.user.get("auth/logout").json();
      } catch (error) {
        console.warn("[Auth] Logout request failed", error);
      } finally {
        tokenManager.removeToken();
        localStorage.removeItem("messages");
        localStorage.removeItem("historyMetadata");
        localStorage.removeItem("sessionInitialized");
        localStorage.removeItem(STORAGE_KEY.AI_ASSISTANT_WS_CLIENT_ID);
        localStorage.removeItem(STORAGE_KEY.AI_ASSISTANT_CONVERSATION_ID);
        localStorage.removeItem(STORAGE_KEY.WS_CLIENT_ID);
        try {
          await clearAssistantStore();
        } catch (error) {
          console.warn(
            "[AI Assistant] Failed to clear assistant storage",
            error,
          );
        }
        try {
          window.dispatchEvent(new CustomEvent("assistant:session-reset"));
        } catch (error) {
          console.warn(
            "[AI Assistant] Failed to broadcast logout reset",
            error,
          );
        }
      }
      if (redirectUrl) {
        const hasReturnTo = /[?&]returnTo=/.test(redirectUrl);
        const returnToParam = `returnTo=${encodeURIComponent(
          CONFIG.endPoints.auth0 || "/",
        )}`;
        const separator = redirectUrl?.includes("?") ? "&" : "?";
        const finalUrl = hasReturnTo
          ? redirectUrl
          : `${redirectUrl}${separator}${returnToParam}`;
        window.location.href = finalUrl as string;
      }
    },
  },
  user: {
    getUserDetail: async (options?: Options) => {
      const response = await apiInstances.user.get("details", options).json();
      return response;
    },
  },
  segments: {
    v2: {
      list: (signal?: AbortSignal): Promise<SegmentListResponse> =>
        apiInstances.user.get('v2/segments', {
          signal: signal ?? getAbortSignalForKey('segments.v2.list'),
        }).json(),

      get: (code: string, signal?: AbortSignal): Promise<Segment> =>
        apiInstances.user.get(`v2/segments/${code}`, { signal }).json(),

      create: (body: SegmentCreateRequest): Promise<Segment> =>
        apiInstances.user.post('v2/segments', {
          json: body,
        }).json(),

      patchDefinition: (code: string, body: SegmentPatchRequest): Promise<Segment> =>
        apiInstances.user.patch(`v2/segments/${code}`, {
          json: body,
        }).json(),

      delete: (code: string): Promise<void> =>
        apiInstances.user.delete(`v2/segments/${code}`).json().then(() => undefined),

      patchPreference: (code: string, body: SegmentPreferencePatchRequest, signal?: AbortSignal): Promise<Segment> =>
        apiInstances.user.patch(`v2/segments/${code}/preference`, {
          json: body,
          signal,
        }).json(),

      categories: {
        create: (body: SegmentCategoryCreateRequest): Promise<SegmentCategory> =>
          apiInstances.user.post('v2/segment-categories', {
            json: body,
          }).json(),
        patch: (code: string, body: SegmentCategoryPatchRequest, signal?: AbortSignal): Promise<SegmentCategory> =>
          apiInstances.user.patch(`v2/segment-categories/${code}`, {
            json: body,
            signal,
          }).json(),
        delete: (code: string, cascade?: boolean): Promise<void> =>
          apiInstances.user
            .delete(`v2/segment-categories/${code}${cascade ? '?cascade=true' : ''}`)
            .json()
            .then(() => undefined),
      },
    },
  },
  attributeLabels: {
    fetch: async (marketIds: number[] = []) => {
      const url = "app/attribute-labels";
      try {
        const response = await apiInstances.user
          .post(url, {
            json: {
              marketIds: marketIds,
            },
            signal: getAbortSignalForKey(url),
          })
          .json();
        return response;
      } catch (err: unknown) {
        if ((err as any).name === "AbortError") {
          return "";
        }
        return { error: "Something went wrong", errorCode: 500 };
      } finally {
        clearAbortController(url);
      }
    },
  },
  brands: {
    updateBrandColor: async ({
      brandId,
      userId,
      marketId,
      color,
      type
    }: {
      brandId: number | string;
      userId: number;
      marketId: number;
      color: string;
      type: string;
    }) => {
      const response = await apiInstances.user
        .post(`${userId}/${marketId}/brands/updateSettings`, {
          json: [
            {
              entityId: brandId,
              position: null,
              color: color,
              entityType: type
            },
          ],
        })
        .json();
      return response;
    },
    updateBrandPosition: async ({
      userId,
      marketId,
      payload,
    }: {
      userId: number;
      marketId: number;
      payload: { position: number; entityId: number | string; entityType?: string }[];
    }) => {
      const response = await apiInstances.user
        .post(`${userId}/${marketId}/brands/updateSettings`, {
          json: payload,
        })
        .json();
      return response;
    },
  },
  markets: {
    updateMarketColor: async ({
      userId,
      marketId,
      color,
    }: {
      userId: number;
      marketId: number;
      color: string;
    }) => {
      const response = await apiInstances.user
        .post(`${userId}/geo/updateSettings`, {
          json: [
            {
              entityId: marketId,
              position: null,
              color: color,
            },
          ],
        })
        .json();
      return response;
    },
    updateMarketOrder: async ({
      userId,
      payload,
    }: {
      userId: number;
      payload: { position: number; entityId: number }[];
    }) => {
      const response = await apiInstances.user
        .post(`${userId}/geo/updateSettings`, {
          json: payload,
        })
        .json();
      return response;
    },
  },
  reports: {
    getReports: async () => {
      const response = await apiInstances.user.get(`reports`).json();
      return response;
    },
    createReport: async (requestPayload: any) => {
      const response = await apiInstances?.user
        .post("report", {
          json: requestPayload,
        })
        .json();

      return response;
    },
    updateReport: async (requestPayload: any) => {
      const response = await apiInstances?.user
        .put("report", {
          json: requestPayload,
        })
        .json();

      return response;
    },
    getReport: async (reportIds: string[]) => {
      const response = await apiInstances.user
        .post("views", {
          json: {
            reportIds: reportIds,
          },
        })
        .json();
      return response;
    },
    getReportViews: async (payload: any) => {
      const response = await apiInstances.user
        .post("report/views", {
          json: payload,
        })
        .json();
      return response;
    },
    deleteReport: async (reportIds: string[], includingViews: boolean) => {
      const response: any = await apiInstances.user
        .delete("reports", {
          json: {
            reportIds,
            deleteAllViews: includingViews,
          },
        })
        .json();
      return response?.length >= 0;
    },
    reorderReports: async (reports: { reportId: string; order: number }[]) => {
      const response: any = await apiInstances.user.post("reports/reorder", {
        json: reports,
      });
      return response === "OK";
    },
  },
  views: {
    createView: async (requestPayload: any) => {
      const response = await apiInstances.user
        .post(`view`, {
          json: requestPayload,
        })
        .json();
      return response;
    },
    updateViewWithData: async (requestPayload: any) => {
      const response = await apiInstances.user
        .put(`view`, {
          json: requestPayload,
        })
        .json();
      return response;
    },
    getReport: async (reportIds: string[]) => {
      const response = await apiInstances.user
        .post("views", {
          json: {
            reportIds: reportIds,
          },
        })
        .json();
      return response;
    },
    updateView: async (viewId: string, requestPayload: any) => {
      const response = await apiInstances.user
        .put(`view/${viewId}`, {
          json: requestPayload,
        })
        .json();
      return response;
    },
    cloneView: async (viewId: string, requestPayload: any) => {
      const response = await apiInstances.user
        .post(`view/clone/${viewId}`, {
          json: requestPayload,
        })
        .json();
      return response;
    },
    getView: async (viewId: string) => {
      const response = await apiInstances.user.get(`view/${viewId}`).json();
      return response;
    },
    getAllViews: async () => {
      const response = await apiInstances.user.get("report/views").json();
      return response;
    },
    saveReportDatePeriod: async (payload: any) => {
      const response = await apiInstances.user
        .post(`view`, {
          json: payload,
        })
        .json();
      return response;
    },
    updateDates: async (payload: any) => {
      const response = await apiInstances.user
        .put(`views`, {
          json: payload,
        })
        .json();
      return response;
    },
    deleteViews: async (viewIds: string[]) => {
      const response = await apiInstances.user
        .delete("views", {
          json: {
            viewIds,
          },
        })
        .json();
      return response === "OK";
    },
    reorderViews: async (
      reportId: string,
      views: { viewId: string; order: number }[],
    ) => {
      const response: any = await apiInstances.user.post(
        `views/report/${reportId}/reorder`,
        {
          json: views,
        },
      );
      return response === "OK";
    },
    validateViews: async (viewId: string) => {
      const response = await apiInstances.user
        .post("view/validate", {
          json: {
            viewId: viewId,
          },
        })
        .json();
      return response;
    },
  },
  brandSets: {
    list: async (): Promise<BrandSetListResponse | BrandSetView[]> => {
      return (await apiInstances.user.get("brand-set").json()) as
        | BrandSetListResponse
        | BrandSetView[];
    },
    create: async (payload: {
      name: string;
      description?: string;
      color?: string;
      weighted: boolean;
      members: {brandId: number, weight: number}[];
    }): Promise<BrandSetView | null> => {
      return (await apiInstances.user
        .post("brand-set", {
          json: payload,
        })
        .json()) as BrandSetView | null;
    },
    update: async (
      brandSetId: string,
      payload: {
        name: string;
        description?: string;
        color?: string;
        weighted: boolean;
        members: { brandId: number; weight: number }[];
      },
    ): Promise<BrandSetView | null> => {
      return (await apiInstances.user
        .put(`brand-set/${brandSetId}`, {
          json: payload,
        })
        .json()) as BrandSetView | null;
    },
    delete: async (brandSetId: string): Promise<unknown> => {
      return apiInstances.user
        .delete(`brand-set/${brandSetId}`)
        .json();
    },
  },
  selections: {
    validate: async (
      body: SelectionsValidateRequest,
    ): Promise<SelectionsValidateResponse> =>
      (await apiInstances.user
        .post("v2/selections/validate", { json: body })
        .json()) as SelectionsValidateResponse,
    apply: async (
      body: SelectionsApplyRequest,
    ): Promise<SelectionsApplyResponse> =>
      (await apiInstances.user
        .post("v2/selections/apply", { json: body })
        .json()) as SelectionsApplyResponse,
  },
  bootstrap: async function () {
    const response: any = await apiInstances.user
      .get("bootstrap/v1/unified")
      .json();
    const brandData = prepareBrandData(
      response?.clients?.[0]?.groups?.[0],
      response?.lookups?.brands,
    );
    const brandsOrderMapping = prepareBrandsMappingData(
      response?.clients?.[0]?.groups?.[0]?.brandOrdersByMarket,
    );
    const marketData = prepareMarketData(
      response?.clients?.[0]?.groups?.[0]?.brands?.[0]?.markets,
      response?.lookups?.markets,
    );

    const featureStatus = prepareFeatureStatusData(
      response?.clients?.[0]?.apps[0],
    );
    
    return {
      brands: brandData,
      brandsOrderMapping,
      marketData,
      user: response?.user,
      featureStatus: featureStatus,
      distributions: response?.clients?.[0]?.distributions ?? null,
      questions: response?.clients?.[0]?.questions ?? null,
      filters: response?.clients?.[0]?.filters ?? null,
    };
  },

};

export default userService;
